import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const PLANS = [
  { key: 'starter', name: 'Starter', price: 0, deliveries: 'No minimum', perDelivery: '$8', color: 'border-gray-700', badge: null, features: ['Pay only when you deliver','No monthly commitment','Real-time driver tracking','SMS & WhatsApp notifications','Driver guaranteed $5 minimum','Cancel anytime'], cta: 'Start Free' },
  { key: 'business', name: 'Business', price: 199, deliveries: '30 deliveries included', perDelivery: '$7 extra', color: 'border-yellow-400', badge: 'Most Popular', features: ['30 deliveries included monthly','$7 per extra delivery','Priority driver matching','SMS & WhatsApp notifications','Driver guaranteed $5 minimum','Cancel anytime'], cta: 'Get Business' },
  { key: 'enterprise', name: 'Enterprise', price: 899, deliveries: '150 deliveries included', perDelivery: '$6.50 extra', color: 'border-green-400/50', badge: 'Best Value', features: ['150 deliveries included monthly','$6.50 per extra delivery','Dedicated account support','SMS & WhatsApp notifications','Driver guaranteed $5 minimum','Cancel anytime'], cta: 'Get Enterprise' },
];

export default function PublicPricing() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Route a plan CTA correctly: logged-in restaurants go straight to billing
  // with the plan pre-selected; everyone else registers first, carrying the
  // chosen plan so they land on billing ready to upgrade after signup.
  function choosePlan(planKey) {
    if (planKey === 'starter') {
      navigate(isAuthenticated ? '/dashboard' : '/register');
      return;
    }
    if (isAuthenticated && user?.role === 'restaurant') {
      navigate(`/billing?plan=${planKey}`);
    } else {
      navigate(`/register?plan=${planKey}`);
    }
  }
  const [volume, setVolume] = useState(30);
  const [estimates, setEstimates] = useState([]);

  useEffect(() => {
    setEstimates(PLANS.map(p => {
      let total = p.key === 'starter' ? volume * 8 : p.key === 'business' ? 199 + Math.max(0, volume-30)*7 : 899 + Math.max(0, volume-150)*6.5;
      return { key: p.key, total, perDelivery: (total/volume).toFixed(2) };
    }));
  }, [volume]);

  const best = estimates.reduce((b, e) => !b || e.total < b.total ? e : b, null);

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="font-black text-2xl text-yellow-400" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</Link>
        <div className="flex items-center gap-3">
          <Link to="/about" className="text-gray-400 hover:text-white text-sm transition">About</Link>
          <Link to="/faq" className="text-gray-400 hover:text-white text-sm transition">FAQ</Link>
          <Link to="/contact" className="text-gray-400 hover:text-white text-sm transition">Contact</Link>
          <Link to="/login" className="text-gray-400 hover:text-white text-sm transition">Sign In</Link>
          <Link to="/register" className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition">Get Started</Link>
        </div>
      </nav>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        <div className="text-center mb-16">
          <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-4">Transparent Pricing</p>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>FLAT RATES.<br /><span className="text-yellow-400">NO SURPRISES.</span></h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">No percentage cuts. No surge pricing. No hidden fees. Every driver is guaranteed $5 minimum per delivery.</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 mb-12">
          <h2 className="font-semibold text-lg mb-2">Cost Estimator</h2>
          <p className="text-gray-400 text-sm mb-6">How many deliveries do you need per month?</p>
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2"><span className="text-gray-400">Monthly deliveries</span><span className="text-yellow-400 font-bold text-lg">{volume}</span></div>
            <input type="range" min={5} max={300} step={5} value={volume} onChange={e => setVolume(parseInt(e.target.value))} className="w-full accent-yellow-400" />
            <div className="flex justify-between text-xs text-gray-600 mt-1"><span>5</span><span>300+</span></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {estimates.map(e => {
              const plan = PLANS.find(p => p.key === e.key);
              const isBest = best?.key === e.key;
              return (
                <div key={e.key} className={`p-4 rounded-xl border text-center ${isBest ? 'border-yellow-400 bg-yellow-400/10' : 'border-gray-700 bg-gray-800/50'}`}>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{plan.name}</p>
                  <p className="text-3xl font-bold text-yellow-400">${e.total}</p>
                  <p className="text-xs text-gray-500 mt-1">${e.perDelivery}/delivery avg</p>
                  {isBest && <p className="text-xs text-green-400 font-semibold mt-2">Best for {volume} deliveries</p>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {PLANS.map(plan => (
            <div key={plan.key} className={`relative border ${plan.color} rounded-2xl p-6 flex flex-col bg-gray-900/60`}>
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-black text-xs font-bold px-4 py-1 rounded-full">{plan.badge}</div>}
              <div className="mb-6">
                <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-2">{plan.name}</p>
                {plan.price === 0 ? <div><span className="text-4xl font-black text-white">{plan.perDelivery}</span><span className="text-gray-400 ml-2 text-sm">per delivery</span></div> : <div><span className="text-4xl font-black text-white">${plan.price}</span><span className="text-gray-400 ml-2 text-sm">per month</span></div>}
                <p className="text-sm text-gray-400 mt-2">{plan.deliveries}</p>
              </div>
              <ul className="space-y-2 mb-8 flex-1">{plan.features.map(f => <li key={f} className="flex items-center gap-2 text-sm text-gray-300"><span className="text-green-400">✓</span>{f}</li>)}</ul>
              <button onClick={() => choosePlan(plan.key)} className={`w-full py-3 rounded-xl font-bold text-sm text-center transition block ${plan.badge ? 'bg-yellow-400 text-black hover:bg-yellow-300' : 'border border-gray-600 text-white hover:border-gray-400'}`}>{plan.cta} →</button>
            </div>
          ))}
        </div>
        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-2xl p-8 text-center mb-16">
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>Every Driver Gets $5 Minimum Per Delivery</h2>
          <p className="text-gray-400 max-w-xl mx-auto">Built into every plan. Drivers are guaranteed a minimum $5 per completed delivery regardless of order size or distance.</p>
        </div>
        <div className="text-center"><p className="text-gray-400 mb-4">Have questions about pricing?</p><Link to="/faq" className="text-yellow-400 hover:underline font-medium">Read our FAQ →</Link></div>
      </main>
      <footer className="border-t border-gray-800 px-4 md:px-8 py-8 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-yellow-400 font-black text-xl" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</p>
          <p className="text-gray-600 text-sm">© 2026 Starfuel Enterprises, Inc. — Tulsa, Oklahoma</p>
          <div className="flex gap-4 text-sm text-gray-500"><Link to="/terms" className="hover:text-white transition">Terms</Link><Link to="/privacy" className="hover:text-white transition">Privacy</Link><Link to="/contact" className="hover:text-white transition">Contact</Link></div>
        </div>
      </footer>
    </div>
  );
}
